import { cloneDeep, orderBy, uniqBy } from 'lodash'
import { OrganisationLevel } from '@/constants/admin/OrganisationLevel'
import { isNullOrEmptyArray } from './array-helpers'

/**
 * Accepts a list of nodes to be retrieved from the loaded organisation
 * tree if they exist. Optionally accepts a tree to be searched instead
 * of the loaded organisation tree.
 *
 * This is useful for ensuring stale/outdated preset selections don't allow
 * the user to retrieve data they don't have access to by only retrieving
 * nodes from the fresh org tree.
 *
 * @param nodesToFind List of nodes to be retrieved from loaded org tree
 * @param tree Tree to be searched
 */
const findNodesByList = (nodesToFind, tree = []) => {
  let result = []
  const seen = new Set()

  function traverse(node) {
    const nodeKey = `${node.id}-${node.level}`
    if (
      nodesToFind.some((n) => n.id === node.id && n.level === node.level) &&
      !seen.has(nodeKey)
    ) {
      result.push(node)
      seen.add(nodeKey)
    }
    if (!isNullOrEmptyArray(node.children)) {
      node.children.forEach((child) => traverse(child))
    }
  }
  for (const node of tree) {
    const nodes = traverse(node)
    if (!isNullOrEmptyArray(nodes)) result = [...result, ...nodes]
  }

  // Remove duplicates from the result array
  const uniqueResult = Array.from(
    new Set(result.map((node) => JSON.stringify(node)))
  ).map((str) => JSON.parse(str))

  return uniqueResult
}

/**
 * Returns all nodes at a specified level.Optionally accepts a tree to be
 * searched instead of the loaded organisation tree.
 *
 * @param targetLevel Requested level
 * @param tree Optional tree to be searched instead of the loaded org tree
 */
const getNodesByLevel = (targetLevel, tree = []) => {
  let result = []

  function traverse(node) {
    if (node.level === targetLevel) {
      result.push(node)
    }

    if (!isNullOrEmptyArray(node.children)) {
      node.children.forEach((child) => traverse(child))
    }
  }

  for (const node of tree) {
    const nodes = traverse(node)
    if (!isNullOrEmptyArray(nodes)) result = [...result, ...nodes]
  }

  return result
}

/**
 * Filters all nodes at a specific level by a search term.
 *
 * Utilises {@link getNodesByLevel} to retrieve all nodes by level
 *
 * @param targetLevel Requested level
 * @param search Search term
 * @param tree Optional tree to be searched instead of the loaded org tree
 */
const searchNodesByLevel = (targetLevel, search, tree = []) => {
  let nodesAtLevel = getNodesByLevel(targetLevel, tree)

  if (!isNullOrEmptyArray(nodesAtLevel)) {
    nodesAtLevel = orderBy(nodesAtLevel, ['title'], ['asc'])
  }

  const trimmedSearchTerm = search?.toLowerCase()?.trim() || ''

  if (!trimmedSearchTerm) return nodesAtLevel

  return nodesAtLevel.filter((selection) =>
    selection.title.toLowerCase().trim().includes(trimmedSearchTerm)
  )
}

/**
 * Removes/prunes all the children nodes at a given level, returning a new
 * tree.
 *
 * This function does not produce side affects as the tree is cloned.
 *
 * @param targetLevel Requested level
 * @param tree Tree object to be traversed
 */
const pruneChildrenNodesAtLevel = (targetLevel, tree) => {
  const clonedTree = cloneDeep(tree)

  // Helper function to recursively prune nodes
  function prune(node) {
    if (node.level === targetLevel) {
      node.children = []
    } else if (node.children && node.children.length > 0) {
      node.children.forEach((child) => prune(child))
    }
  }

  // Start pruning from the root node
  for (const node of clonedTree) {
    prune(node)
  }
  return clonedTree
}

const traverseAndSearchTree = (node, id, level) => {
  // Check if the current node matches the id and level
  if (node.id === id && node.level === level) {
    return node
  }

  // Recursively traverse the children
  if (node.children && node.children.length > 0) {
    for (const child of node.children) {
      const result = traverseAndSearchTree(child, id, level)
      if (result) {
        return result
      }
    }
  }

  // Return null if no matching node is found
  return null
}

/**
 * Searches the org tree for a specific node by id and level.
 * Returning the node if found.
 *
 * @param id ID of requested node
 * @param level Level of requested node
 * @param tree Optional tree to be searched instead of the loaded org tree
 */
const searchTree = (id, level, tree = []) => {
  for (const node of tree) {
    const found = traverseAndSearchTree(node, id, level)
    if (found) return found
  }

  return null
}

/**
 * This function first finds the starting node based on the provided {@link startId}
 * and {@link startLevel}. Then, it traverses the tree from that node to collect all
 * nodes at the {@link targetLevel}.
 *
 * @param {string} targetLevel Level of leaf/children nodes to be returned
 * @param {string} startId Id of node to begin search
 * @param {string} startLevel Level of node to begin search
 * @param {Array} tree
 * @returns
 */
function findLeafNodesAtLevel(targetLevel, startId, startLevel, tree) {
  const startNode = searchTree(startId, startLevel, tree)

  if (!startNode) return []

  return getNodesByLevel(targetLevel, startNode.children)
}

/**
 * Creates a unique key for all nodes at the target level. This new key is comprised
 * of all the ids of the target level nodes' parents.
 * @param {*} tree
 * @param {*} targetLevel
 * @returns
 */
function generateUniqueIdForNodesAtTargetLevelByParentIds(tree, targetLevel) {
  function traverse(node, childIds = []) {
    const ids = [node.id, ...childIds]

    if (node.level === targetLevel) {
      const newId = ids.join('-')
      node.originalKey = node.key // preserve original id
      node.key = newId // Update the id of the node at the target level
    } else if (node.children) {
      for (const child of node.children) {
        traverse(child, ids)
      }
    }
  }

  const clonedTree = cloneDeep(tree)

  for (const branch of clonedTree) {
    traverse(branch)
  }

  return clonedTree
}

/**
 * Removes duplicate admins from a selection. This is required as part of the
 * duplicate admin id fix left the same admin selected multiple times.
 *
 * @param {Array} selection
 * @returns
 */
function filterDuplicateAdminNodes(selection) {
  if (isNullOrEmptyArray(selection)) return []

  const nonAdminNodes = selection.filter(
    (node) => node.level !== OrganisationLevel.Admin
  )
  const adminNodes = selection.filter(
    (node) => node.level === OrganisationLevel.Admin
  )

  const uniqueAdminNodes = uniqBy(adminNodes, 'id')

  return nonAdminNodes.concat(uniqueAdminNodes)
}

export {
  findNodesByList,
  findLeafNodesAtLevel,
  getNodesByLevel,
  pruneChildrenNodesAtLevel,
  searchTree,
  searchNodesByLevel,
  generateUniqueIdForNodesAtTargetLevelByParentIds,
  filterDuplicateAdminNodes,
}
